import { useMemo } from "react"
import { AlertCircle, ArrowRight, Briefcase } from "react-feather"
import { useNavigate } from "react-router-dom"
import type { OwnerSummary200TicketCounts } from "@/api/generated/models"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { EmptyCard, PendingTaskCard } from "@/components/ui/card"
import { Spinner } from "@/components/ui/spinner/Spinner"

interface PendingTasksSectionProps {
  ticketCounts?: OwnerSummary200TicketCounts
  isLoading?: boolean
}

export function PendingTasksSection({ ticketCounts, isLoading }: PendingTasksSectionProps) {
  const navigate = useNavigate()

  const pendingTasks = useMemo(() => {
    if (!ticketCounts) return []

    const tasks = [
      {
        id: "1",
        title: "未読チャット",
        count: ticketCounts.unreadChatCount,
        helpText:
          "オペレーションセンターから未読のチャットが届いています。チャットを確認し、必要に応じて返信をしてください。",
        onClick: () => navigate("/tickets?showOnlyUnread=true"),
      },
      {
        id: "2",
        title: "オーナー様対応中",
        count: ticketCounts.ownerInProgressCount,
        helpText: "現在オーナー様にて対応が必要な案件があります。確認してください。",
        onClick: () => navigate("/tickets?status=owner_in_progress"),
      },
      {
        id: "3",
        title: "新規入居者情報承認",
        count: ticketCounts.newTenantApprovalCount,
        helpText: (
          <>
            新しい入居者情報が届いています。承認を押してサービスを開始してください。
            <br />
            ※承認をしなければコールセンターサービスがご利用いただけません
          </>
        ),
        onClick: () => navigate("/tickets?category=shinki-nyukyosha-shonin&excludeCompleted=true"),
      },
      {
        id: "4",
        title: "解約済みか確認が必要な契約",
        count: ticketCounts.cancellationConfirmationRequiredCount,
        helpText: (
          <>
            コールセンターに解約の問い合わせがあった方で解約処理が行われていない契約があります。
            <br />
            サービスが継続しており課金が発生しています。内容を確認して解約に関する処理を行ってください。
          </>
        ),
        onClick: () => navigate("/tickets?category=kaiyaku-uketsuke&excludeCompleted=true"),
      },
      {
        id: "5",
        title: "オーナー様指定業者の未完了案件",
        count: ticketCounts.ownerSpecifiedContractorIncompleteCount,
        helpText:
          "オーナー様指定業者で対応を行っている案件があります。対応が完了したら、案件の作業完了ボタンより処理をしてください。",
        onClick: () => navigate("/tickets?contractorType=owner_specified&excludeCompleted=true"),
      },
    ]

    // カウントが0より大きいタスクのみ表示
    return tasks.filter((task) => task.count > 0)
  }, [ticketCounts, navigate])

  const hasPendingTasks = pendingTasks.length > 0

  if (isLoading) {
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-slate-400" />
          <h2 className="text-base font-semibold text-[#354267]">要対応案件</h2>
        </div>
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Briefcase className="h-4 w-4 text-slate-400" />
        <h2 className="text-base font-semibold text-[#354267]">要対応案件</h2>
      </div>

      {!hasPendingTasks && <EmptyCard text="要対応案件はありません" />}

      {hasPendingTasks && (
        <>
          <Alert className="bg-slate-100 border-slate-400">
            <AlertCircle className="h-4 w-4 text-[#354267]" />
            <AlertDescription className="text-[#354267] font-semibold text-xs">
              対応が必要な案件があります
            </AlertDescription>
          </Alert>

          <PendingTaskCard tasks={pendingTasks} />

          <div className="flex justify-end">
            <Button
              variant="ghost"
              size="sm"
              className="text-[#354267] font-bold hover:bg-transparent"
              onClick={() => navigate("/tickets")}
            >
              一覧を開く
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
